import type { FC, InputHTMLAttributes, ReactNode } from "react";
import { twMerge } from "tailwind-merge";

import { BUTTON_EVENT_CATEGORY, type TrackingProps, trackEvent } from "utils/amplitude";

type RadioButtonCardProps = InputHTMLAttributes<HTMLInputElement> &
  TrackingProps & {
    children?: ReactNode;
    icon: ReactNode;
    label: ReactNode;
    description?: ReactNode;
    containerClassName?: string;
  };

// TODO: disabled state
export const RadioButtonCard: FC<RadioButtonCardProps> = ({
  children,
  containerClassName,
  description,
  icon,
  label,
  eventName,
  eventProps = {},
  onChange,
  id,
  ...props
}) => {
  const handleChange = eventName
    ? (e: React.ChangeEvent<HTMLInputElement>) => {
        if (onChange) onChange(e);
        trackEvent(eventName, {
          ...BUTTON_EVENT_CATEGORY,
          ...eventProps,
        });
      }
    : onChange;

  const isChecked = props.checked ?? props.defaultChecked;

  return (
    <label
      className={twMerge(
        "flex cursor-pointer items-start gap-3 rounded-[12px] border px-[18px] py-4",
        isChecked ? "border-blue-tint-40 bg-blue-tint-95" : "border-black-tint-90 bg-white",
        props.disabled && "cursor-not-allowed opacity-50",
        containerClassName,
      )}
      htmlFor={id}
    >
      <input className="mt-0.5 shrink-0" id={id} type="radio" {...props} onChange={handleChange} />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-center gap-2">
          <span className="shrink-0 text-black-tint-40">{icon}</span>
          <span className="body-s text-black-tint-20">{label}</span>
        </div>

        {description && <span className="body-xs text-black-tint-40">{description}</span>}

        {children && (
          <div className="mt-2 w-full" onClick={(e) => e.stopPropagation()}>
            {children}
          </div>
        )}
      </div>
    </label>
  );
};

RadioButtonCard.displayName = "RadioButtonCard";
